import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, map } from 'rxjs';
import { AppHttpService } from '../../services/app-http.service';
import { API_MAP } from '../../core/api-map/api-map';
import { BaseCardData } from '../../../../../card-templates/src/lib/card-templates/models/base-card-data.model';
import { GeneralMetaData } from '../../../../../card-templates/src/lib/card-templates/models/general.model';
import { BaseCardPage, BaseTemplateData } from '../../../../../card-templates/src/lib/card-templates/models/template';
import { switchMap, tap } from 'rxjs/operators';
import { CarderUser } from '../../shared/auth/models';
import { AuthStore } from '../../shared/auth/services';
import { encodeToBase64 } from 'card-templates';

export interface CardCreateDto {
  ownerId: string;
  businessId?: string;
  templateId?: string;
  template: BaseTemplateData;
  general?: GeneralMetaData;
  slug?: string;
  isPublished?: boolean;
  isDraft?: boolean;
  usedFeatures?: string[];
  analytics?: {
    views: number;
    clicks: number;
    lastViewed: string | null;
  };
  // Card metadata for easy identification and comparison
  cardName?: string;
  cardDescription?: string;
  cardTarget?: string;
}

export interface CardMetadataUpdateDto {
  cardName?: string;
  cardDescription?: string;
  cardTarget?: string;
}

// Draft data interface for auto-save functionality
export interface CardDraft {
  general: GeneralMetaData;
  template: BaseTemplateData;
  savedAt: string;
  version: number;
}

// Draft save DTO
export interface DraftSaveDto {
  general?: GeneralMetaData;
  template?: BaseTemplateData;
}

// Draft status response
export interface DraftStatusResponse {
  hasDraft: boolean;
  savedAt?: string;
  version?: number;
}

// Slug suggestions response
export interface SlugSuggestionsResponse {
  suggestions: string[];
}

// Using BaseCardData instead of custom Card interface
export interface Card extends BaseCardData {
  _id: string;
  ownerId: string;
  businessId?: string;
  assignedToStaffId: string;
  templateId: string;
  slug?: string;
  isPublished: boolean;
  isDraft: boolean;
  usedFeatures: string[];
  analytics: {
    views: number;
    clicks: number;
    lastViewed: string | null;
    leads: number;
  };
  createdAt: string;
  updatedAt: string;
  qrCodeBase64: string;
  // Card metadata for easy identification and comparison
  cardName?: string;
  cardDescription?: string;
  cardTarget?: string;
  // Custom domain attached to the card
  customDomain?: string;
  // Card snapshots for Creative Hub
  cardSnapshotImage?: string;
  qrSnapshotImage?: string;
  snapshotsGeneratedAt?: string;
  // Draft for auto-save functionality (only for published cards)
  draft?: CardDraft;
}

@Injectable({
  providedIn: 'root'
})
export class BusinessCardsService {
  // BehaviorSubject to store the cards
  private cardsSubject = new BehaviorSubject<Card[]>([]);

  // Observable to expose the cards to components
  public cards$ = this.cardsSubject.asObservable();
  staffCards$ = this.cards$.pipe(
    switchMap(cards => this.authStore.user$.pipe(
      map((user: CarderUser) => {
        return cards.filter((c => c.ownerId !== user._id))
      }
      )
    )),
  );

  // Getter for current cards value
  get cards(): Card[] {
    return this.cardsSubject.value;
  }

  constructor(private http: AppHttpService, private authStore: AuthStore) {
  }

  /**
   * Clean up drag and drop template data before sending to server
   * - Sets backgroundGradient to null when backgroundMode is not 'gradient'
   * - Removes empty or invalid gradient objects
   * - Fills in default values for incomplete gradient objects
   */
  private sanitizeDragDropData(data: any): any {
    if (!data || !data.elements) {
      return data;
    }

    return {
      ...data,
      elements: data.elements.map((element: any) => {
        const sanitizedElement = { ...element };

        // Clean up backgroundGradient when not in gradient mode
        if (sanitizedElement.backgroundMode !== 'gradient') {
          sanitizedElement.backgroundGradient = null;
        } else if (sanitizedElement.backgroundGradient) {
          // Gradient mode is enabled - ensure gradient object is complete
          const gradient = sanitizedElement.backgroundGradient;
          const hasValidColorStops = gradient.colorStops &&
            Array.isArray(gradient.colorStops) &&
            gradient.colorStops.length >= 2;

          if (!hasValidColorStops) {
            // Invalid gradient - set to null
            sanitizedElement.backgroundGradient = null;
          } else {
            // Valid colorStops - fill in missing required fields with defaults
            // Also strip MongoDB _id fields from colorStops
            sanitizedElement.backgroundGradient = {
              enabled: gradient.enabled ?? true,
              type: gradient.type || 'linear',
              direction: gradient.direction || 'to-bottom-right',
              angle: gradient.angle,
              colorStops: gradient.colorStops.map((stop: any) => ({
                color: stop.color,
                colorType: stop.colorType,
                position: stop.position
              }))
            };
          }
        }

        return sanitizedElement;
      })
    };
  }

  /**
   * Encode custom template HTML and CSS to Base64 before sending to server
   * Also sanitizes drag-and-drop template data
   * @param cardData Card data with potential custom template
   * @returns Card data with Base64-encoded template fields and sanitized drag-drop data
   */
  private encodeCardTemplateData<T extends { template?: BaseTemplateData }>(cardData: T): T {
    const encoded = { ...cardData };

    // Sanitize drag-and-drop template data if present
    if (encoded.template?.dragAndDropTemplate) {
      encoded.template = {
        ...encoded.template,
        dragAndDropTemplate: this.sanitizeDragDropData(encoded.template.dragAndDropTemplate)
      };
    }

    // Encode custom template HTML and CSS to Base64
    if (encoded.template?.customTemplate) {
      const customTemplate = encoded.template.customTemplate;
      if (customTemplate.generatedHtml) {
        customTemplate.generatedHtml = encodeToBase64(customTemplate.generatedHtml);
      }
      if (customTemplate.generatedCss) {
        customTemplate.generatedCss = encodeToBase64(customTemplate.generatedCss);
      }
    }

    return encoded;
  }

  /**
   * Create a new card
   * @param businessId Business ID
   * @param card Card data to create
   * @returns Observable of the created card
   */
  createCard(businessId: string, card: CardCreateDto): Observable<Card> {
    // Encode custom template HTML and CSS to Base64 before sending
    const encodedCard = this.encodeCardTemplateData(card);

    return this.http.request<Card>({
      ...API_MAP.createCard,
      path: API_MAP.createCard.path.replace(':businessId', businessId),
      body: encodedCard
    });
  }

  /**
   * Get all cards
   * @returns Observable of cards array
   */
  getCards(): Observable<Card[]> {
    return this.http.request<Card[]>(API_MAP.getCards);
  }

  /**
   * Get business cards using the new API endpoint
   * @param businessId Business ID
   * @param ownerId Optional owner ID to filter cards by specific user
   * @returns Observable of cards array
   */
  getBusinessCards(businessId: string, ownerId?: string): Observable<Card[]> {
    let path = API_MAP.getBusinessCards.path.replace(':businessId', businessId);

    if (ownerId) {
      path += `?ownerId=${ownerId}`;
    }

    return this.http.request<Card[]>({
      ...API_MAP.getBusinessCards,
      path
    });
  }

  /**
   * Get cards by owner ID (deprecated - use getBusinessCards instead)
   * @param ownerId Owner ID
   * @returns Observable of cards array
   * @deprecated Use getBusinessCards with ownerId parameter instead
   */
  getCardsByOwnerId(ownerId: string): Observable<Card[]> {
    return this.http.request<Card[]>({
      ...API_MAP.getCards,
      path: `${API_MAP.getCards.path}?ownerId=${ownerId}`
    });
  }

  /**
   * Get cards by business ID (deprecated - use getBusinessCards instead)
   * @param businessId Business ID
   * @returns Observable of cards array
   * @deprecated Use getBusinessCards instead
   */
  getCardsByBusinessId(businessId: string): Observable<Card[]> {
    return this.http.request<Card[]>({
      ...API_MAP.getCards,
      path: `${API_MAP.getCards.path}?businessId=${businessId}`
    }).pipe(
      tap(cards => {
        // Update the cards BehaviorSubject
        this.cardsSubject.next(cards);
      })
    );
  }

  /**
   * Load cards for a business and update the cards BehaviorSubject
   * @param businessId Business ID
   * @param ownerId Optional owner ID to filter cards by specific user
   */
  loadCardsForBusiness(businessId: string, ownerId?: string): void {
    if (!businessId) {
      this.cardsSubject.next([]);
      return;
    }

    this.getBusinessCards(businessId, ownerId).subscribe({
      next: (cards) => {
        this.cardsSubject.next(cards);
      },
      error: (err) => {
        console.error('Error loading cards for business:', err);
        this.cardsSubject.next([]);
      }
    });
  }

  setCards(cards: Card[]): void {
    this.cardsSubject.next(cards);
  }

  /**
   * Get card by ID
   * @param id Card ID
   * @returns Observable of card
   */
  getCardById(id: string, businessId: string): Observable<Card> {
    return this.http.request<Card>({
      ...API_MAP.getCardById,
      path: API_MAP.getCardById.path.replace(':id', id).replace(':businessId', businessId)
    });
  }

  /**
   * Get card by slug
   * @param slug Card slug
   * @returns Observable of card
   */
  getCardBySlug(slug: string): Observable<Card> {
    return this.http.request<Card>({
      ...API_MAP.getCardBySlug,
      path: API_MAP.getCardBySlug.path.replace(':slug', slug)
    });
  }

  /**
   * Update card
   * @param businessId Business ID
   * @param id Card ID
   * @param card Card data to update
   * @returns Observable of updated card
   */
  updateCard(businessId: string, id: string, card: Partial<CardCreateDto>): Observable<Card> {
    // Encode custom template HTML and CSS to Base64 before sending
    console.log('card: ', card);
    const encodedCard = this.encodeCardTemplateData(card);

    return this.http.request<Card>({
      ...API_MAP.updateCard,
      path: API_MAP.updateCard.path.replace(':id', id).replace(':businessId', businessId),
      body: encodedCard
    });
  }

  /**
   * Update card metadata (name, description, target)
   * @param businessId Business ID
   * @param id Card ID
   * @param metadata Metadata to update
   * @returns Observable of updated card
   */
  updateCardMetadata(businessId: string, id: string, metadata: CardMetadataUpdateDto): Observable<Card> {
    return this.http.request<Card>({
      ...API_MAP.updateCardMetadata,
      path: API_MAP.updateCardMetadata.path.replace(':id', id).replace(':businessId', businessId),
      body: metadata
    });
  }

  /**
   * Update card chatData (AI agent configuration)
   * @param businessId Business ID
   * @param id Card ID
   * @param chatData Chat data to update
   * @returns Observable of updated card
   */
  updateCardChatData(businessId: string, id: string, chatData: any): Observable<Card> {
    return this.http.request<Card>({
      ...API_MAP.updateCardChatData,
      path: API_MAP.updateCardChatData.path.replace(':id', id).replace(':businessId', businessId),
      body: chatData
    });
  }

  /**
   * Delete card
   * @param businessId Business ID
   * @param id Card ID
   * @returns Observable of deleted card
   */
  deleteCard(businessId: string, id: string): Observable<void> {
    return this.http.request<void>({
      ...API_MAP.deleteCard,
      path: API_MAP.deleteCard.path.replace(':id', id).replace(':businessId', businessId)
    });
  }

  /**
   * Publish card
   * @param businessId Business ID
   * @param id Card ID
   * @returns Observable of published card
   */
  publishCard(businessId: string, id: string): Observable<Card> {
    return this.http.request<Card>({
      ...API_MAP.publishCard,
      path: API_MAP.publishCard.path.replace(':id', id).replace(':businessId', businessId)
    });
  }

  /**
   * Unpublish card
   * @param businessId Business ID
   * @param id Card ID
   * @returns Observable of unpublished card
   */
  unpublishCard(businessId: string, id: string): Observable<Card> {
    return this.http.request<Card>({
      ...API_MAP.unpublishCard,
      path: API_MAP.unpublishCard.path.replace(':id', id).replace(':businessId', businessId)
    });
  }

  /**
   * Record card view
   * @param id Card ID
   * @returns Observable of card with updated view count
   */
  recordCardView(id: string): Observable<Card> {
    return this.http.request<Card>({
      ...API_MAP.viewCard,
      path: API_MAP.viewCard.path.replace(':id', id)
    });
  }

  /**
   * Record card click
   * @param id Card ID
   * @returns Observable of card with updated click count
   */
  recordCardClick(id: string): Observable<Card> {
    return this.http.request<Card>({
      ...API_MAP.clickCard,
      path: API_MAP.clickCard.path.replace(':id', id)
    });
  }

  /**
   * Switch published card
   * @param businessId Business ID
   * @param fromCardId ID of the currently published card to unpublish
   * @param toCardId ID of the card to publish
   * @returns Observable of the newly published card
   */
  switchPublishedCard(businessId: string, fromCardId: string, toCardId: string): Observable<Card> {
    return this.http.request<Card>({
      ...API_MAP.publishSwitchCard,
      path: API_MAP.publishSwitchCard.path.replace(':businessId', businessId),
      body: {
        fromCardId,
        toCardId
      }
    });
  }

  /**
   * Save draft changes for a published card (auto-save functionality)
   * Only works for cards that are already published.
   * @param businessId Business ID
   * @param cardId Card ID
   * @param draftData Draft data to save (general and/or template)
   * @returns Observable of updated card with draft
   */
  saveDraft(businessId: string, cardId: string, draftData: DraftSaveDto): Observable<Card> {
    // Encode custom template HTML and CSS to Base64 before sending
    const encodedDraft = this.encodeCardTemplateData(draftData as any) as DraftSaveDto;

    return this.http.request<Card>({
      ...API_MAP.saveDraft,
      path: API_MAP.saveDraft.path
        .replace(':id', cardId)
        .replace(':businessId', businessId),
      body: encodedDraft
    });
  }

  /**
   * Discard draft and revert to published version
   * @param businessId Business ID
   * @param cardId Card ID
   * @returns Observable of card without draft
   */
  discardDraft(businessId: string, cardId: string): Observable<Card> {
    return this.http.request<Card>({
      ...API_MAP.discardDraft,
      path: API_MAP.discardDraft.path
        .replace(':id', cardId)
        .replace(':businessId', businessId)
    });
  }

  /**
   * Get draft status for a card
   * @param businessId Business ID
   * @param cardId Card ID
   * @returns Observable of draft status
   */
  getDraftStatus(businessId: string, cardId: string): Observable<DraftStatusResponse> {
    return this.http.request<DraftStatusResponse>({
      ...API_MAP.getDraftStatus,
      path: API_MAP.getDraftStatus.path
        .replace(':id', cardId)
        .replace(':businessId', businessId)
    });
  }

  /**
   * Get slug suggestions for a card
   * @param businessId Business ID
   * @param cardId Card ID
   * @param baseName Optional base slug to generate variations from
   * @returns Observable of slug suggestions
   */
  getSlugSuggestions(
    businessId: string,
    cardId: string,
    baseName?: string
  ): Observable<SlugSuggestionsResponse> {
    let path = API_MAP.getSlugSuggestions.path
      .replace(':id', cardId)
      .replace(':businessId', businessId);

    if (baseName) {
      path += `?baseName=${encodeURIComponent(baseName)}`;
    }

    return this.http.request<SlugSuggestionsResponse>({
      ...API_MAP.getSlugSuggestions,
      path
    });
  }
}
